import { createContext, useContext, useEffect, useMemo, useSyncExternalStore } from "react";
import { useAppStore } from "../stores/appStore";
import useLogManager from "../hooks/useLogManager";
import { useScheduledJobs } from "../hooks/useScheduledJobs";
import { useDailyRuntimeReset } from "../hooks/useDailyRuntimeReset";
import { API_TRANSPORT, getProxySettings, pauseProxyExecution, subscribeProxySettings } from '../services/proxySettings.js';

const TaskRuntimeContext = createContext(null);

export const TaskRuntimeProvider = ({ children }) => {
  const taskManager = useAppStore((state) => state.taskManager);
  const proxySettings = useSyncExternalStore(subscribeProxySettings, getProxySettings, getProxySettings);
  const transportPaused = API_TRANSPORT === 'proxy' && (proxySettings.paused || Boolean(taskManager?.transportPaused));
  const logManager = useLogManager({
    batchSize: 100,
    persistKey: "daily_tasks_logs",
    enablePersist: true,
  });
  const scheduledJobs = useScheduledJobs(taskManager, logManager.addLog, { enabled: !transportPaused });

  useEffect(() => {
    if (!taskManager) return undefined;
    taskManager.setLogFunction(logManager.addLog);
    return () => taskManager.setLogFunction(null);
  }, [taskManager, logManager.addLog]);

  useDailyRuntimeReset(
    taskManager,
    logManager.addLog,
    scheduledJobs.rescheduleScheduledJobs
  );

  const value = useMemo(() => ({
    ...logManager,
    ...scheduledJobs,
    transportPaused,
    resumeProxyExecution: () => {
      const recovery = useAppStore.getState().proxyRecovery;
      if (!recovery) throw new Error('请先等待应用初始化或刷新页面');
      return recovery.resume();
    },
    stopProxyRecovery: () => {
      taskManager?.discardTransportCheckpoint();
      pauseProxyExecution('已停止自动恢复，待续跑任务已取消', 'USER_STOP');
    },
  }), [logManager, scheduledJobs, transportPaused, taskManager]);

  return (
    <TaskRuntimeContext.Provider value={value}>
      {children}
    </TaskRuntimeContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useTaskRuntime = () => {
  const runtime = useContext(TaskRuntimeContext);
  if (!runtime) {
    throw new Error("useTaskRuntime 必须在 TaskRuntimeProvider 中使用");
  }
  return runtime;
};
